<template>
  <EditorTotalPrice v-bind="{ hasPromoCode, playCount, total }" @click="isOpen = true" />

  <LcBottomSheet v-model="isOpen" :max-height="MAX_SHEET_HEIGHT" :snap-heights="['auto']">
    <template #title>
      <h2 class="headline-h5-headline">{{ $t('editor.pricedetails.headline') }}</h2>
    </template>

    <EditorPriceDetailsList
      v-if="currentPrice"
      v-bind="{ currentPrice, flaggedCount, isSubscription, lotteryId, prepaidSubscriptionAmount }"
    />

    <template #footer>
      <LcSeparator class="mt-none mb-200" />

      <LcDescriptionList class="gap-050">
        <dt class="headline-h5-headline">{{ $t('pricelist.labels.subtotal') }}</dt>
        <dd class="headline-h5-headline text-right" data-testid="price-details-subtotal">
          {{ subtotal.toString() }}
        </dd>

        <template v-if="showConvenienceFee">
          <dt class="flex gap-100 items-center">
            {{ convenienceFeeLabel }}

            <LcInfoButton
              :key="`infobutton_${locale}`"
              :headline="$t('infobutton.conveniencefee.headline')"
              :open-button-aria-label="$t('infobutton.conveniencefee.label')"
              tabindex="-1"
              :text="$t('infobutton.conveniencefee.message')"
            />
          </dt>
          <dd class="text-right" data-testid="price-details-fee">
            {{ convenienceFee.toString() }}
          </dd>
        </template>

        <template v-if="showSalesTax">
          <dt>{{ $t('pricelist.labels.salestax') }}</dt>
          <dd class="text-right" data-testid="price-details-salestax">
            {{ salesTax.toString() }}
          </dd>
        </template>
      </LcDescriptionList>

      <EditorPriceDetailsPromotion v-if="promotionTitle" class="mt-200" :title="promotionTitle" />
    </template>
  </LcBottomSheet>
</template>

<script lang="ts" setup>
import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

const MAX_SHEET_HEIGHT = '95dvh'

type Props = {
  lotteryId: LotteryId
}

const { lotteryId } = defineProps<Props>()

const { locale, t } = useI18n()

const {
  convenienceFee,
  currentPrice,
  flaggedCount,
  hasPromoCode,
  isPriceTransparencyEnabled,
  isSubscription,
  playCount,
  prepaidSubscriptionAmount,
  promotionTitle,
  salesTax,
  subtotal,
  total,
} = useEditorPriceDetails(lotteryId)

const isOpen = ref(false)

const showConvenienceFee = computed(() => convenienceFee.value.value > 0)

const showSalesTax = computed(() => salesTax.value.value > 0)

const convenienceFeeLabel = isPriceTransparencyEnabled
  ? t('common.words.conveniencefee')
  : t('pricelist.labels.conveniencefee')
</script>
